import {
  AlignStack,
  Button,
  colors,
  darkThemeSelector,
  devices,
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuPopover,
  DropdownMenuTrigger,
  firstChildSelector,
  FocusRing,
  HStack,
  Icon,
  lastChildSelector,
  Small,
  styled,
  Text,
  ToggleInput,
  Tooltip,
  UtilityControl,
} from '@meterup/atto';
import { selectors } from '@meterup/atto/src/controls/shared/styles';
import { useMemo, useState } from 'react';

import type {
  DateRangeFilter,
  Filter,
  MultiSelectFilter,
  NumberFilter,
  NumberRangeFilter,
  SelectFilter,
  Table,
  TableFilterDefinition,
  TextFilter,
} from '../utils';
import { parseDateTime } from '../../../topics/time';
import { FormatDateTime } from '../../FormatDateTime';
import { TableFilterVariant } from '../utils';
import { ServerTableFiltersField } from './TableFiltersFields';

const TableFiltersTokenHandleLabel = styled(Small, {
  fontWeight: '$bold',
  whiteSpace: 'nowrap',
});
TableFiltersTokenHandleLabel.displayName = 'TableFiltersTokenHandleLabel';

const TableFiltersTokenHandle = styled(AlignStack, {
  position: 'relative',
  padding: '$2 $6',
});
TableFiltersTokenHandle.displayName = 'TableFiltersTokenHandle';

const TableFiltersTokenValueLabel = styled(Small, {
  fontWeight: '$bold',
});
TableFiltersTokenValueLabel.displayName = 'TableFiltersTokenValueLabel';

const TableFiltersTokenValueArrows = styled(Icon, {
  width: '$10',
  height: '$10',
});
TableFiltersTokenValueArrows.displayName = 'TableFiltersTokenValueArrows';

const TableFiltersTokenValue = styled('button', FocusRing, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  padding: '$2 $6',

  [firstChildSelector]: {
    borderTopLeftRadius: '$8',
    borderBottomLeftRadius: '$8',
  },

  [lastChildSelector]: {
    borderTopRightRadius: '$8',
    borderBottomRightRadius: '$8',
  },

  [selectors.focus]: {
    borderRadius: '$8',
  },

  variants: {
    modified: {
      true: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },

        [`& ${TableFiltersTokenValueLabel}`]: {
          color: colors.bodyBrandLight,

          [darkThemeSelector]: {
            color: colors.bodyBrandDark,
          },
        },

        [`& ${TableFiltersTokenValueArrows}`]: {
          color: colors.iconBrandLight,

          [darkThemeSelector]: {
            color: colors.iconBrandDark,
          },
        },

        [selectors.hover]: {
          [`& ${TableFiltersTokenValueLabel}`]: {
            color: colors.headingBrandLight,

            [darkThemeSelector]: {
              color: colors.headingBrandDark,
            },
          },

          [`& ${TableFiltersTokenValueArrows}`]: {
            color: colors.bodyBrandLight,

            [darkThemeSelector]: {
              color: colors.bodyBrandDark,
            },
          },
        },
      },
      false: {
        background: colors.bgNeutralLight,
        strokeAll: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: colors.strokeNeutralDark,
        },

        [`& ${TableFiltersTokenValueLabel}`]: {
          color: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            color: colors.bodyNeutralDark,
          },
        },

        [`& ${TableFiltersTokenValueArrows}`]: {
          color: colors.iconNeutralLight,

          [darkThemeSelector]: {
            color: colors.iconNeutralDark,
          },
        },

        [selectors.hover]: {
          [`& ${TableFiltersTokenValueLabel}`]: {
            color: colors.headingNeutralLight,

            [darkThemeSelector]: {
              color: colors.headingNeutralDark,
            },
          },

          [`& ${TableFiltersTokenValueArrows}`]: {
            color: colors.bodyNeutralLight,

            [darkThemeSelector]: {
              color: colors.bodyNeutralDark,
            },
          },
        },
      },
    },
  },
});
TableFiltersTokenValue.displayName = 'TableFiltersTokenValue';

const TableFiltersTokenTrigger = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  minHeight: '$24',
  strokeAll: colors.strokeNeutralLight,
  borderRadius: '$8',

  [darkThemeSelector]: {
    strokeAll: colors.strokeNeutralDark,
  },
});
TableFiltersTokenTrigger.displayName = 'TableFiltersTokenTrigger';

const TableFiltersTokenHeading = styled(AlignStack, {
  padding: '$2 $10',
});
TableFiltersTokenHeading.displayName = 'TableFiltersTokenHeading';

/**
 * Perches on the token's top-right corner so a filter can be removed without opening it. Toggle
 * tokens have no popover, and so no home for the usual "Clear filter" button — this is their only
 * way out. It's absolutely positioned, so the row measures the same whether or not it's showing.
 */
const TableFiltersTokenRemove = styled(UtilityControl, {
  position: 'absolute',
  zIndex: 2,
  top: 0,
  right: 0,
  // Mostly clear of the token rather than centred on its corner: a token is only 24px tall, and
  // its value — a toggle, on the variants that get this — runs right up to the trailing edge. The
  // upward nudge is small because the row scrolls horizontally, so it clips on both axes, and
  // there are just 6px of padding above a token to poke out into.
  transform: 'translate(70%, -25%)',
  opacity: 0,
  pointerEvents: 'none',

  // Neutral while it's only announcing itself, and opaque: a utility control is translucent by
  // default, which smudges into the token stroke it sits over. Doubled up to outrank the variant
  // styles it's replacing.
  '&&': {
    background: colors.bgNeutralLight,
    outlineColor: colors.strokeNeutralLight,
    $$iconColor: colors.iconNeutralLight,

    // A step down from the usual inline control, which at 20px is most of a 24px token's height.
    [devices.mouse]: {
      size: '$16',
    },

    [devices.touch]: {
      size: '$24',
    },

    [darkThemeSelector]: {
      background: colors.bgNeutralDark,
      outlineColor: colors.strokeNeutralDark,
      $$iconColor: colors.iconNeutralDark,
    },
  },

  // Turning red only once you're on it keeps the badge from reading as an alarm every time the
  // cursor crosses a filter. Tripled up: the variant's own hover styles are already two classes.
  [`&&&:hover, &&&:focus-visible, &&&:has(:focus-visible), &&&[data-focus-visible='true']`]: {
    background: colors.bgNegativeLight,
    outlineColor: colors.strokeNegativeLight,
    $$iconColor: colors.bodyNegativeLight,

    [darkThemeSelector]: {
      background: colors.bgNegativeDark,
      outlineColor: colors.strokeNegativeDark,
      $$iconColor: colors.bodyNegativeDark,
    },
  },

  [devices.touch]: {
    // Nothing to hover with, so it just stays out.
    opacity: 1,
    pointerEvents: 'auto',
  },
});
TableFiltersTokenRemove.displayName = 'TableFiltersTokenRemove';

const TableFiltersTokenContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',

  // Revealed from CSS rather than `useUtilityVisibility`: the hook attaches a `mousemove` listener
  // per instance, and a filter row holds a handful of these. `:focus-within` covers the keyboard,
  // and the control is a tab stop even while transparent, so it can be reached before it appears.
  [`&:hover ${TableFiltersTokenRemove}, &:focus-within ${TableFiltersTokenRemove}`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },
});
TableFiltersTokenContainer.displayName = 'TableFiltersTokenContainer';

type BaseTokenProps = {
  disabled?: boolean;
  changeable?: boolean;
  dismissable?: boolean;
  handle?: React.ReactNode;
  modified?: boolean;
  value?: React.ReactNode;
  children?: React.ReactNode;
  onTokenClear: React.MouseEventHandler<HTMLButtonElement>;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

type DropdownTokenProps = BaseTokenProps & {
  variant?: 'dropdown';
  onToggleClick?: never;
  toggleSelected?: never;
};

type ToggleTokenProps = BaseTokenProps & {
  variant: 'toggle';
  onToggleClick: () => void;
  toggleSelected: boolean;
};

/** A toggle that answers from the bar, plus a dropdown refining the toggled-on state. */
type ToggleDropdownTokenProps = BaseTokenProps & {
  variant: 'toggle-dropdown';
  onToggleClick: () => void;
  toggleSelected: boolean;
};

export type TableFiltersTokenProps =
  | DropdownTokenProps
  | ToggleTokenProps
  | ToggleDropdownTokenProps;

export function TableFiltersToken({
  changeable,
  disabled = false,
  dismissable = true,
  handle,
  modified = true,
  value,
  children,
  onTokenClear,
  isOpen,
  onOpenChange,
  variant,
  onToggleClick,
  toggleSelected,
  ...restProps
}: TableFiltersTokenProps) {
  if (variant === 'toggle') {
    return (
      <TableFiltersTokenContainer {...restProps}>
        <TableFiltersTokenTrigger>
          <TableFiltersTokenHandle align="center" width="min-content" preset="small">
            {handle && <TableFiltersTokenHandleLabel>{handle}</TableFiltersTokenHandleLabel>}
          </TableFiltersTokenHandle>
          {/* A toggle always holds a value, so only a selected one counts as modifying the table. */}
          <TableFiltersTokenValue tabIndex={-1} modified={modified && toggleSelected}>
            <ToggleInput
              size="small"
              selected={toggleSelected}
              onChange={onToggleClick}
              disabled={disabled}
            />
          </TableFiltersTokenValue>
        </TableFiltersTokenTrigger>
        {dismissable && !disabled && (
          <TableFiltersTokenRemove
            onClick={onTokenClear}
            icon="trash-can"
            label="Clear filter"
            variant="neutral"
          />
        )}
      </TableFiltersTokenContainer>
    );
  }

  if (variant === 'toggle-dropdown') {
    const toggleSegment = (
      <TableFiltersTokenValue tabIndex={-1} modified={modified && toggleSelected}>
        <ToggleInput
          size="small"
          selected={toggleSelected}
          onChange={onToggleClick}
          disabled={disabled}
        />
      </TableFiltersTokenValue>
    );
    const handleSegment = (
      <TableFiltersTokenHandle align="center" width="min-content" preset="small">
        {handle && <TableFiltersTokenHandleLabel>{handle}</TableFiltersTokenHandleLabel>}
      </TableFiltersTokenHandle>
    );

    return (
      <TableFiltersTokenContainer {...restProps}>
        {/* The dropdown refines the toggled-on state, so switched off it has nothing to say. */}
        {toggleSelected ? (
          <DropdownMenu open={isOpen} onOpenChange={onOpenChange} modal={false} disabled={disabled}>
            <TableFiltersTokenTrigger>
              {handleSegment}
              {toggleSegment}
              <DropdownMenuTrigger
                disabled={disabled}
                render={(triggerProps) => (
                  <TableFiltersTokenValue
                    {...triggerProps}
                    tabIndex={0}
                    // The toggle owns the boolean, so this segment only lights up over a refinement.
                    modified={modified && value != null}
                  >
                    <TableFiltersTokenValueLabel whitespace="no-wrap">
                      {value ?? 'All'}
                    </TableFiltersTokenValueLabel>
                    {changeable && <TableFiltersTokenValueArrows icon="chevrons-vertical" />}
                  </TableFiltersTokenValue>
                )}
              />
            </TableFiltersTokenTrigger>
            <DropdownMenuPopover align="start" side="bottom" sideOffset={8}>
              {dismissable && (
                <DropdownMenuGroup>
                  <TableFiltersTokenHeading
                    align="center"
                    gap={6}
                    preset="small"
                    width="100%"
                    end={
                      <Button
                        onClick={onTokenClear}
                        appearance="muted"
                        arrangement="hidden-label"
                        icon="trash-can"
                        ghost
                        size="x-small"
                        variant="destructive"
                        disabled={disabled}
                      >
                        Clear filter
                      </Button>
                    }
                  >
                    <Small weight="bold">{handle}</Small>
                  </TableFiltersTokenHeading>
                </DropdownMenuGroup>
              )}
              <DropdownMenuGroup scrollable>{children}</DropdownMenuGroup>
            </DropdownMenuPopover>
          </DropdownMenu>
        ) : (
          <TableFiltersTokenTrigger>
            {handleSegment}
            {toggleSegment}
          </TableFiltersTokenTrigger>
        )}
        {dismissable && !disabled && (
          <TableFiltersTokenRemove
            onClick={onTokenClear}
            icon="trash-can"
            label="Clear filter"
            variant="neutral"
          />
        )}
      </TableFiltersTokenContainer>
    );
  }

  return (
    <TableFiltersTokenContainer {...restProps}>
      <DropdownMenu open={isOpen} onOpenChange={onOpenChange} modal={false} disabled={disabled}>
        <TableFiltersTokenTrigger>
          <TableFiltersTokenHandle align="center" width="min-content" preset="small">
            {handle && <TableFiltersTokenHandleLabel>{handle}</TableFiltersTokenHandleLabel>}
          </TableFiltersTokenHandle>
          <DropdownMenuTrigger
            disabled={disabled}
            render={(triggerProps) => (
              <TableFiltersTokenValue {...triggerProps} tabIndex={0} modified={modified}>
                <TableFiltersTokenValueLabel whitespace="no-wrap">
                  {value ?? 'All'}
                </TableFiltersTokenValueLabel>
                {changeable && <TableFiltersTokenValueArrows icon="chevrons-vertical" />}
              </TableFiltersTokenValue>
            )}
          />
        </TableFiltersTokenTrigger>
        <DropdownMenuPopover align="start" side="bottom" sideOffset={8}>
          {dismissable && (
            <DropdownMenuGroup>
              <TableFiltersTokenHeading
                align="center"
                gap={6}
                preset="small"
                width="100%"
                end={
                  <Button
                    onClick={onTokenClear}
                    appearance="muted"
                    arrangement="hidden-label"
                    icon="trash-can"
                    ghost
                    size="x-small"
                    variant="destructive"
                    disabled={disabled}
                  >
                    Clear filter
                  </Button>
                }
              >
                <Small weight="bold">{handle}</Small>
              </TableFiltersTokenHeading>
            </DropdownMenuGroup>
          )}
          <DropdownMenuGroup scrollable>{children}</DropdownMenuGroup>
        </DropdownMenuPopover>
      </DropdownMenu>
    </TableFiltersTokenContainer>
  );
}

type SelectServerTableFiltersTokenValueProps = {
  filter: SelectFilter;
  definition: TableFilterDefinition;
};

function SelectServerTableFiltersTokenValue({
  definition,
  filter,
}: SelectServerTableFiltersTokenValueProps) {
  return (
    <TableFiltersTokenValueLabel whitespace="no-wrap">
      {definition.options.find((opt) => opt.value === filter.value)?.value ?? 'All'}
    </TableFiltersTokenValueLabel>
  );
}

type MultiSelectServerTableFiltersTokenValueProps = {
  filter: MultiSelectFilter;
  definition: TableFilterDefinition;
};

function MultiSelectServerTableFiltersTokenValue({
  definition,
  filter,
}: MultiSelectServerTableFiltersTokenValueProps) {
  return (
    <TableFiltersTokenValueLabel whitespace="no-wrap">
      <HStack spacing={4}>
        {filter.values.length > 0
          ? filter.values.map((v, index) => (
              <HStack key={v} wrap="nowrap" align="center">
                {definition.options.find((opt) => opt.value === v)?.label ?? v}
                {index < filter.values.length - 1 ? <Text>,</Text> : null}
              </HStack>
            ))
          : 'All'}
      </HStack>
    </TableFiltersTokenValueLabel>
  );
}

type DateRangeServerTableFiltersTokenValueProps = {
  filter: DateRangeFilter;
};

function DateRangeServerTableFiltersTokenValue({
  filter,
}: DateRangeServerTableFiltersTokenValueProps) {
  const startDate = parseDateTime(filter.start);
  const endDate = parseDateTime(filter.end);
  return (
    <TableFiltersTokenValueLabel whitespace="no-wrap">
      {startDate || endDate ? (
        <HStack align="center" spacing={4} wrap="nowrap">
          <FormatDateTime value={startDate} />
          <Icon icon="arrow-right" size={10} />
          <FormatDateTime value={endDate} />
        </HStack>
      ) : (
        'All'
      )}
    </TableFiltersTokenValueLabel>
  );
}

type NumberRangeServerTableFiltersTokenValueProps = {
  filter: NumberRangeFilter;
};

function NumberRangeServerTableFiltersTokenValue({
  filter,
}: NumberRangeServerTableFiltersTokenValueProps) {
  return (
    <TableFiltersTokenValueLabel whitespace="no-wrap">
      {filter.start || filter.end ? (
        <HStack align="center" spacing={4} wrap="nowrap">
          {filter.start}
          <Icon icon="arrow-right" size={10} />
          {filter.end}
        </HStack>
      ) : (
        'All'
      )}
    </TableFiltersTokenValueLabel>
  );
}

type NumberServerTableFiltersTokenValueProps = {
  filter: NumberFilter;
};

function NumberServerTableFiltersTokenValue({ filter }: NumberServerTableFiltersTokenValueProps) {
  return (
    <TableFiltersTokenValueLabel whitespace="no-wrap">
      {filter.value ? filter.value : 'All'}
    </TableFiltersTokenValueLabel>
  );
}

type TextServerTableFiltersTokenValueProps = {
  filter: TextFilter;
};

function TextServerTableFiltersTokenValue({ filter }: TextServerTableFiltersTokenValueProps) {
  return (
    <TableFiltersTokenValueLabel whitespace="no-wrap">
      {filter.value ? filter.value : 'All'}
    </TableFiltersTokenValueLabel>
  );
}

type ServerTableFiltersTokenValueProps = {
  filter: Filter;
  definition: TableFilterDefinition;
};

function ServerTableFiltersTokenValue({ definition, filter }: ServerTableFiltersTokenValueProps) {
  switch (filter.type) {
    case TableFilterVariant.Bool:
      return null;
    case TableFilterVariant.DateRange:
      return <DateRangeServerTableFiltersTokenValue filter={filter} />;
    case TableFilterVariant.MultiSelect:
      return <MultiSelectServerTableFiltersTokenValue definition={definition} filter={filter} />;
    case TableFilterVariant.Number:
      return <NumberServerTableFiltersTokenValue filter={filter} />;
    case TableFilterVariant.NumberRange:
      return <NumberRangeServerTableFiltersTokenValue filter={filter} />;
    case TableFilterVariant.Select:
      return <SelectServerTableFiltersTokenValue definition={definition} filter={filter} />;
    case TableFilterVariant.Text:
      return <TextServerTableFiltersTokenValue filter={filter} />;
  }
}

type ServerTableFiltersTokenProps<R> = {
  filter: Filter;
  table: Table<R>;
};

export function ServerTableFiltersToken<R>({ filter, table }: ServerTableFiltersTokenProps<R>) {
  const isModified = useMemo(() => {
    switch (filter.type) {
      case TableFilterVariant.DateRange:
        return !filter.start && !filter.end;
      case TableFilterVariant.MultiSelect:
        return filter.values.length > 0;
      case TableFilterVariant.Number:
        return true;
      case TableFilterVariant.NumberRange:
        return !filter.start && !filter.end;
      case TableFilterVariant.Select:
        return filter.value.length > 0;
      case TableFilterVariant.Text:
        return filter.value.length > 0;
      default:
        return true;
    }
  }, [filter]);
  const [isOpen, setIsOpen] = useState(true);
  const definition = table.getFilterDefinition(filter.key);
  if (!definition) {
    return null;
  }
  switch (filter.type) {
    case TableFilterVariant.Bool:
      return (
        <TableFiltersTokenContainer>
          <TableFiltersTokenTrigger>
            <TableFiltersTokenHandle align="center" width="min-content" preset="small">
              <TableFiltersTokenHandleLabel>{definition.label}</TableFiltersTokenHandleLabel>
            </TableFiltersTokenHandle>
            <Tooltip contents={filter.value ? 'Yes' : 'No'}>
              <TableFiltersTokenValue tabIndex={-1} modified={filter.value}>
                <ToggleInput
                  size="small"
                  selected={filter.value}
                  onChange={(isSelected) =>
                    table.updateFilter({
                      key: filter.key,
                      value: isSelected,
                      type: TableFilterVariant.Bool,
                    })
                  }
                />
              </TableFiltersTokenValue>
            </Tooltip>
          </TableFiltersTokenTrigger>
          <TableFiltersTokenRemove
            onClick={() => table.clearFilter(filter.key)}
            icon="trash-can"
            label="Clear filter"
            variant="neutral"
          />
        </TableFiltersTokenContainer>
      );
    case TableFilterVariant.DateRange:
    case TableFilterVariant.MultiSelect:
    case TableFilterVariant.Number:
    case TableFilterVariant.NumberRange:
    case TableFilterVariant.Select:
    case TableFilterVariant.Text:
      return (
        <TableFiltersTokenContainer>
          <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
            <TableFiltersTokenTrigger>
              <TableFiltersTokenHandle align="center" width="min-content" preset="small">
                <TableFiltersTokenHandleLabel>{definition.label}</TableFiltersTokenHandleLabel>
              </TableFiltersTokenHandle>
              <DropdownMenuTrigger
                render={(triggerProps) => (
                  <TableFiltersTokenValue {...triggerProps} tabIndex={0} modified={isModified}>
                    <ServerTableFiltersTokenValue filter={filter} definition={definition} />
                    <TableFiltersTokenValueArrows icon="chevrons-vertical" />
                  </TableFiltersTokenValue>
                )}
              />
            </TableFiltersTokenTrigger>
            {/* Portaled by default: inline popovers misalign inside HorizontalScrollShadow (overflow: auto). */}
            <DropdownMenuPopover align="start" side="bottom" sideOffset={8}>
              <DropdownMenuGroup>
                <TableFiltersTokenHeading
                  align="center"
                  gap={6}
                  preset="small"
                  width="100%"
                  end={
                    <Button
                      onClick={() => table.clearFilter(filter.key)}
                      appearance="muted"
                      arrangement="hidden-label"
                      icon="trash-can"
                      ghost
                      size="x-small"
                      variant="destructive"
                    >
                      Clear filter
                    </Button>
                  }
                >
                  <Small weight="bold">{definition.label}</Small>
                </TableFiltersTokenHeading>
              </DropdownMenuGroup>
              <DropdownMenuGroup scrollable>
                <ServerTableFiltersField table={table} definition={definition} filter={filter} />
              </DropdownMenuGroup>
            </DropdownMenuPopover>
          </DropdownMenu>
        </TableFiltersTokenContainer>
      );
  }
}
